/**
 * Prospecting Campaign Templates
 * Based on best practices from Outreach, Apollo.io, Salesloft, Gong
 *
 * Key insights from billion-dollar cold outreach companies:
 * - Problem-first messaging gets 3x higher reply rates (Apollo.io)
 * - Trigger-based outreach converts 2-4x better (ZoomInfo)
 * - Personalized subject lines increase opens by 26% (HubSpot)
 * - 3-email sequences have the best cost/benefit ratio
 * - Multi-channel (email + LinkedIn) increases response 2.5x
 */

import { CampaignTemplate } from "./types";

export const PROBLEM_AWARE_OPENER: CampaignTemplate = {
  id: "prospect-problem-aware",
  name: "Problem-Aware Opener",
  category: "prospecting",
  description:
    "Lead with the prospect's pain point, not your product. The most effective cold email pattern.",
  strategy:
    "This approach mirrors HubSpot's GPCTBA/C&I qualification framework. Lead with a problem you've observed in their industry, show you understand their world, then hint at a solution. Apollo.io reports problem-first messaging gets 3x higher reply rates than product-first.",
  icon: "AimOutlined",
  color: "#3b82f6",
  targetAudience: "b2b-saas",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "15-25% open rate",
    "3-8% reply rate",
    "2-5% positive reply rate",
    "Higher quality conversations",
  ],

  bestPractices: [
    "Research the prospect's company for 2-3 minutes before sending",
    "Reference a specific trigger (new hire, funding, content they published)",
    "Keep subject line under 40 characters",
    "Send Tuesday-Thursday, 8-10 AM recipient's local time",
    "Use a single, clear CTA - don't offer multiple options",
  ],

  objectionHandling: [
    "No budget: Focus on ROI and offer pilot program",
    "Using competitor: Highlight specific differentiators without badmouthing",
    "Not the right time: Offer to follow up in 30/60/90 days",
    "Need to involve others: Ask for warm introduction to decision maker",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "referral", "event"],
    companySize: ["11-50", "51-200", "201-500"],
    industries: ["SaaS", "Technology", "E-commerce", "Professional Services"],
    jobTitles: [
      "VP of Marketing",
      "Head of Sales",
      "Director of Customer Success",
      "CMO",
      "CRO",
    ],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Problem Hook",
      subject: "{{first_name}}, quick question about {{pain_point}}",
      suggestedMjmlTemplateId: "coldOutreach-problemAware",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Hook with a problem they recognize, hint at solution",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "pain_point",
          defaultValue: "customer engagement",
          description: "The main problem they face",
          required: true,
        },
        {
          key: "industry_insight",
          defaultValue:
            "Many companies in your space struggle with converting website visitors into conversations",
          description: "Industry-specific observation",
        },
      ],
      abTestSuggestions: {
        subjectVariants: [
          "{{first_name}}, noticed something about {{company_name}}",
          "Question about {{company_name}}'s approach to {{pain_point}}",
          "Thought about {{company_name}} today",
        ],
      },
    },
    {
      name: "Day 3 - Value Add",
      subject: "Re: {{pain_point}} at {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-valueAdd",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Provide value with insight, resource, or data point",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "resource_name",
          defaultValue: "Guide to Modern Customer Engagement",
          description: "Valuable resource to share",
        },
        {
          key: "stat",
          defaultValue:
            "Companies using AI chatbots see 40% more qualified conversations",
          description: "Relevant statistic",
        },
      ],
    },
    {
      name: "Day 7 - Social Proof",
      subject: "How {{case_study_company}} solved {{pain_point}}",
      suggestedMjmlTemplateId: "coldOutreach-socialProof",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Show proof that your solution works for similar companies",
      variables: [
        {
          key: "case_study_company",
          defaultValue: "a company like yours",
          description: "Similar company success story",
        },
        {
          key: "case_study_result",
          defaultValue: "40% increase in qualified leads",
          description: "Quantified result",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 50,
    dailyLimit: 150,
    requiresWarming: true,
    minDomainAge: 30,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's full name",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "Account Executive",
      description: "Sales rep's title",
      source: "manual",
    },
    {
      key: "sender_company",
      defaultValue: "Conferbot",
      description: "Your company name",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Calendly or meeting booking link",
      source: "crm",
    },
  ],

  tags: ["prospecting", "problem-aware", "cold-outreach", "b2b", "high-converting"],
  bestFor: ["B2B SaaS Sales", "SDR Teams", "Account Executives", "Outbound Sales"],
  difficulty: "beginner",
  setupTime: "10 minutes",
  inspiredBy: ["HubSpot", "Apollo.io", "Outreach"],
};

export const SOLUTION_AWARE_INTRO: CampaignTemplate = {
  id: "prospect-solution-aware",
  name: "Solution-Aware Introduction",
  category: "prospecting",
  description:
    "For prospects already aware of solutions in your category. Position your unique value.",
  strategy:
    "This template works when prospects are actively researching solutions. Lead with your unique differentiation and specific outcomes. Gartner research shows solution-aware buyers respond 2x better to outcome-focused messaging than feature lists.",
  icon: "BulbOutlined",
  color: "#10b981",
  targetAudience: "b2b-saas",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "12-20% open rate",
    "2-6% reply rate",
    "Shorter sales cycles",
    "Higher-intent conversations",
  ],

  bestPractices: [
    "Research what solutions they're currently evaluating",
    "Lead with outcomes, not features",
    "Include specific numbers (ROI, time saved, etc.)",
    "Offer a quick comparison or demo",
    "Keep email under 100 words",
  ],

  segmentation: {
    leadSource: ["website", "referral", "event", "crm"],
    companySize: ["51-200", "201-500", "501-1000"],
    industries: ["SaaS", "Technology", "Financial Services"],
    jobTitles: [
      "Head of Customer Support",
      "VP of Operations",
      "Director of Digital",
    ],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Differentiation Hook",
      subject: "{{first_name}}, different approach to {{solution_category}}",
      suggestedMjmlTemplateId: "coldOutreach-solutionAware",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Position unique value vs. alternatives they may be considering",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "solution_category",
          defaultValue: "customer engagement",
          description: "The solution category",
        },
        {
          key: "unique_value",
          defaultValue: "AI that actually understands context",
          description: "Your key differentiator",
        },
      ],
    },
    {
      name: "Day 3 - Outcome Focus",
      subject: "Re: {{result_metric}} in {{timeframe}}",
      suggestedMjmlTemplateId: "coldOutreach-outcomeProof",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Share specific outcomes achieved by similar companies",
      variables: [
        {
          key: "result_metric",
          defaultValue: "40% more conversions",
          description: "Key result metric",
        },
        {
          key: "timeframe",
          defaultValue: "30 days",
          description: "Time to achieve result",
        },
      ],
    },
    {
      name: "Day 6 - Quick Demo Offer",
      subject: "15-min comparison: {{company_name}} vs alternatives",
      suggestedMjmlTemplateId: "coldOutreach-demoOffer",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Offer personalized comparison or demo",
      variables: [
        {
          key: "demo_focus",
          defaultValue: "how it would work for your specific use case",
          description: "Demo focus area",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 50,
    dailyLimit: 150,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "Solutions Consultant",
      description: "Sales rep's title",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["prospecting", "solution-aware", "differentiation", "outcome-focused"],
  bestFor: ["Solution Selling", "Technical Sales", "Competitive Deals"],
  difficulty: "intermediate",
  setupTime: "15 minutes",
  inspiredBy: ["Gong", "Chorus.ai", "Salesloft"],
};

export const COMPETITOR_DISPLACEMENT: CampaignTemplate = {
  id: "prospect-competitor-displacement",
  name: "Competitor Displacement",
  category: "prospecting",
  description:
    "Target users of competitor products with strategic switch messaging.",
  strategy:
    "This campaign targets prospects using a specific competitor. Never badmouth competitors - instead, focus on what's changed in the market and how your approach is different. Klue research shows displacement campaigns convert 40% better when they lead with 'what's new' vs. 'what's wrong'.",
  icon: "SwapOutlined",
  color: "#f59e0b",
  targetAudience: "b2b-saas",
  personalizationLevel: "high",

  expectedOutcomes: [
    "10-18% open rate",
    "2-5% reply rate",
    "Higher deal values (switching = serious intent)",
    "Faster evaluation cycles",
  ],

  bestPractices: [
    "Never directly criticize competitors",
    "Focus on what's changed in the market",
    "Highlight migration ease and support",
    "Use specific feature comparisons, not generalities",
    "Offer a pilot or proof-of-concept",
  ],

  objectionHandling: [
    "Switching costs too high: Offer migration assistance and ROI calculator",
    "Current solution works fine: Focus on opportunity cost of not improving",
    "Under contract: Ask when renewal is, offer to reconnect then",
  ],

  segmentation: {
    leadSource: ["linkedin", "purchased", "scraped", "crm"],
    companySize: ["51-200", "201-500", "501-1000"],
    industries: ["SaaS", "Technology", "E-commerce"],
    jobTitles: ["VP of Marketing", "Head of Digital", "CTO", "CMO"],
    requiredFields: [
      "first_name",
      "company_name",
      "email",
      "current_solution",
    ],
  },

  emails: [
    {
      name: "Day 0 - The Market Has Changed",
      subject: "{{first_name}}, something's changed since you chose {{competitor}}",
      suggestedMjmlTemplateId: "coldOutreach-competitorSwitch",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Plant seed of doubt without attacking competitor",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "competitor",
          defaultValue: "your current solution",
          description: "Competitor name",
          required: true,
        },
        {
          key: "market_change",
          defaultValue: "AI has fundamentally changed what's possible",
          description: "What's changed in the market",
        },
      ],
    },
    {
      name: "Day 4 - Specific Comparison",
      subject: "{{competitor}} vs. us: honest comparison",
      suggestedMjmlTemplateId: "coldOutreach-comparison",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Provide specific, honest comparison",
      variables: [
        {
          key: "key_difference_1",
          defaultValue: "AI that actually understands context",
          description: "First key difference",
        },
        {
          key: "key_difference_2",
          defaultValue: "No-code customization",
          description: "Second key difference",
        },
      ],
    },
    {
      name: "Day 8 - Migration Made Easy",
      subject: "How {{similar_company}} switched in 2 weeks",
      suggestedMjmlTemplateId: "coldOutreach-migrationStory",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Address switching concerns with success story",
      variables: [
        {
          key: "similar_company",
          defaultValue: "a company like yours",
          description: "Company that successfully switched",
        },
        {
          key: "migration_time",
          defaultValue: "2 weeks",
          description: "Time to migrate",
        },
      ],
    },
    {
      name: "Day 12 - ROI Calculator",
      subject: "What switching would actually save {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-roiCalculator",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Quantify the value of switching",
      variables: [
        {
          key: "potential_savings",
          defaultValue: "significant cost and time",
          description: "Estimated savings",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 40,
    dailyLimit: 120,
    requiresWarming: true,
    minDomainAge: 45,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "competitor",
      defaultValue: "your current solution",
      description: "Competitor name",
      source: "enrichment",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting link",
      source: "crm",
    },
  ],

  tags: ["prospecting", "competitive", "displacement", "switching"],
  bestFor: ["Competitive Markets", "Established Categories", "Enterprise Sales"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["Klue", "Crayon", "Compete"],
};

export const TRIGGER_FUNDING: CampaignTemplate = {
  id: "prospect-trigger-funding",
  name: "Funding Announcement Trigger",
  category: "prospecting",
  description:
    "Reach out to companies immediately after they announce funding rounds.",
  strategy:
    "Freshly funded companies have budget and mandate to grow. Reaching out within 48 hours of funding announcement gets 4x higher response rates (ZoomInfo data). Congratulate genuinely, then connect funding to growth challenges you solve.",
  icon: "RiseOutlined",
  color: "#22c55e",
  targetAudience: "startup",
  personalizationLevel: "high",

  expectedOutcomes: [
    "25-35% open rate",
    "5-12% reply rate",
    "Budget conversations happen faster",
    "Higher average deal size",
  ],

  bestPractices: [
    "Reach out within 48 hours of announcement",
    "Congratulate genuinely before pitching",
    "Connect your solution to their growth plans",
    "Research what they plan to use funds for",
    "Keep it personal - they're getting lots of outreach",
  ],

  segmentation: {
    leadSource: ["scraped", "manual", "crm"],
    companySize: ["11-50", "51-200"],
    industries: ["SaaS", "Technology", "Fintech", "Healthcare Tech"],
    jobTitles: ["CEO", "CTO", "VP of Growth", "Head of Sales"],
    requiredFields: ["first_name", "company_name", "email", "funding_amount"],
  },

  emails: [
    {
      name: "Day 0 - Congrats + Connection",
      subject: "Congrats on the {{funding_amount}} raise, {{first_name}}!",
      suggestedMjmlTemplateId: "coldOutreach-triggerFunding",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Genuine congratulations + soft connection to growth challenge",
      optimalSendTime: {
        dayOfWeek: ["monday", "tuesday", "wednesday", "thursday", "friday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "funding_amount",
          defaultValue: "funding",
          description: "Funding amount (e.g., $10M Series A)",
          required: true,
        },
        {
          key: "growth_challenge",
          defaultValue: "scaling customer engagement",
          description: "Growth challenge you help with",
        },
      ],
    },
    {
      name: "Day 3 - Growth Use Case",
      subject: "How {{similar_company}} scaled post-funding",
      suggestedMjmlTemplateId: "coldOutreach-growthStory",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Share how similar company scaled after funding",
      variables: [
        {
          key: "similar_company",
          defaultValue: "a similar startup",
          description: "Company with similar funding story",
        },
        {
          key: "growth_result",
          defaultValue: "3x their customer engagement in 90 days",
          description: "Growth result achieved",
        },
      ],
    },
    {
      name: "Day 7 - Specific Offer",
      subject: "Quick win for {{company_name}}'s growth plans",
      suggestedMjmlTemplateId: "coldOutreach-quickWin",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Offer specific, quick-to-implement solution",
      variables: [
        {
          key: "quick_win",
          defaultValue: "launch an AI chatbot in under a week",
          description: "Quick win you can deliver",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 30,
    dailyLimit: 90,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "funding_amount",
      defaultValue: "your recent funding",
      description: "Funding round details",
      source: "enrichment",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting link",
      source: "crm",
    },
  ],

  tags: ["prospecting", "trigger", "funding", "startup", "growth"],
  bestFor: ["Startup-focused Sales", "Growth Stage Companies", "Venture Ecosystem"],
  difficulty: "intermediate",
  setupTime: "15 minutes",
  inspiredBy: ["ZoomInfo", "Crunchbase", "PitchBook"],
};

export const TRIGGER_HIRING: CampaignTemplate = {
  id: "prospect-trigger-hiring",
  name: "Hiring Signal Trigger",
  category: "prospecting",
  description:
    "Target companies actively hiring for roles that indicate need for your solution.",
  strategy:
    "Job postings reveal company priorities and pain points. A company hiring 5+ customer success reps needs better tools. LinkedIn data shows hiring signal outreach gets 3x better response than generic outreach because it's timely and relevant.",
  icon: "TeamOutlined",
  color: "#8b5cf6",
  targetAudience: "b2b-saas",
  personalizationLevel: "high",

  expectedOutcomes: [
    "20-30% open rate",
    "4-10% reply rate",
    "Highly relevant conversations",
    "Strong buying intent signals",
  ],

  bestPractices: [
    "Reference the specific job posting you saw",
    "Connect hiring to the problem you solve",
    "Reach out within 1 week of job posting",
    "Target the hiring manager, not HR",
    "Show how you help scale without as much hiring",
  ],

  segmentation: {
    leadSource: ["linkedin", "scraped", "manual"],
    companySize: ["51-200", "201-500", "501-1000"],
    industries: ["SaaS", "Technology", "E-commerce", "Financial Services"],
    jobTitles: [
      "VP of Customer Success",
      "Head of Support",
      "Director of Operations",
    ],
    requiredFields: ["first_name", "company_name", "email", "hiring_role"],
  },

  emails: [
    {
      name: "Day 0 - Hiring Observation",
      subject: "{{first_name}}, noticed you're hiring {{hiring_role}}s",
      suggestedMjmlTemplateId: "coldOutreach-triggerHiring",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Connect job posting to scaling challenge you solve",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "hiring_role",
          defaultValue: "Customer Support Rep",
          description: "Role they're hiring for",
          required: true,
        },
        {
          key: "scaling_challenge",
          defaultValue: "handling growing customer volume",
          description: "Challenge the hiring addresses",
        },
      ],
    },
    {
      name: "Day 3 - Scale Without Hiring",
      subject: "What if you could handle 2x volume without 2x headcount?",
      suggestedMjmlTemplateId: "coldOutreach-scaleEfficiency",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Present alternative to hiring - efficiency",
      variables: [
        {
          key: "efficiency_stat",
          defaultValue: "handle 60% more conversations without adding headcount",
          description: "Efficiency improvement possible",
        },
      ],
    },
    {
      name: "Day 7 - Case Study",
      subject: "How {{case_study_company}} scaled support 3x",
      suggestedMjmlTemplateId: "coldOutreach-socialProof",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Show how similar company scaled with your solution",
      variables: [
        {
          key: "case_study_company",
          defaultValue: "a growing SaaS company",
          description: "Similar company example",
        },
        {
          key: "scale_result",
          defaultValue: "3x customer volume with same team size",
          description: "Scaling result achieved",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 40,
    dailyLimit: 120,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "hiring_role",
      defaultValue: "Support Representatives",
      description: "Role being hired",
      source: "enrichment",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting link",
      source: "crm",
    },
  ],

  tags: ["prospecting", "trigger", "hiring", "scaling", "efficiency"],
  bestFor: ["Growing Companies", "Scale-up Sales", "Operations Leaders"],
  difficulty: "intermediate",
  setupTime: "15 minutes",
  inspiredBy: ["LinkedIn Sales Navigator", "ZoomInfo", "Lusha"],
};

export const TRIGGER_TECH_CHANGE: CampaignTemplate = {
  id: "prospect-trigger-tech-change",
  name: "Tech Stack Change Trigger",
  category: "prospecting",
  description:
    "Target companies that recently adopted complementary technology.",
  strategy:
    "Companies changing their tech stack are 5x more likely to buy additional solutions. If they just adopted HubSpot, they're thinking about their customer stack. BuiltWith and Siftery data enables precise timing for outreach.",
  icon: "ApiOutlined",
  color: "#06b6d4",
  targetAudience: "b2b-saas",
  personalizationLevel: "high",

  expectedOutcomes: [
    "18-28% open rate",
    "4-9% reply rate",
    "Natural conversation starters",
    "Integration-focused deals",
  ],

  bestPractices: [
    "Reference the specific technology they adopted",
    "Show how you integrate/complement their new tool",
    "Time outreach 2-4 weeks after adoption",
    "Focus on maximizing their tech investment",
    "Offer integration-focused demo",
  ],

  segmentation: {
    leadSource: ["scraped", "purchased", "crm"],
    companySize: ["51-200", "201-500", "501-1000"],
    industries: ["SaaS", "Technology", "E-commerce"],
    jobTitles: ["Head of RevOps", "VP of Marketing", "CTO", "Director of IT"],
    requiredFields: [
      "first_name",
      "company_name",
      "email",
      "new_technology",
    ],
  },

  emails: [
    {
      name: "Day 0 - Tech Adoption Notice",
      subject: "{{first_name}}, noticed {{company_name}} added {{new_technology}}",
      suggestedMjmlTemplateId: "coldOutreach-triggerTech",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Connect tech adoption to complementary value you provide",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "new_technology",
          defaultValue: "new CRM",
          description: "Technology they recently adopted",
          required: true,
        },
        {
          key: "complementary_value",
          defaultValue: "maximize your investment with AI-powered engagement",
          description: "How you complement their tech",
        },
      ],
    },
    {
      name: "Day 4 - Integration Value",
      subject: "Getting more from {{new_technology}} + AI chatbots",
      suggestedMjmlTemplateId: "coldOutreach-integrationValue",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Show specific integration benefits",
      variables: [
        {
          key: "integration_benefit",
          defaultValue: "auto-qualify leads and sync to your CRM in real-time",
          description: "Key integration benefit",
        },
      ],
    },
    {
      name: "Day 8 - Stack Optimization",
      subject: "How {{similar_company}} optimized their stack",
      suggestedMjmlTemplateId: "coldOutreach-stackOptimization",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Show how similar company maximized tech investment",
      variables: [
        {
          key: "similar_company",
          defaultValue: "a company with similar stack",
          description: "Company with similar tech stack",
        },
        {
          key: "optimization_result",
          defaultValue: "2x ROI from their CRM investment",
          description: "Result of stack optimization",
        },
      ],
    },
    {
      name: "Day 12 - Integration Demo",
      subject: "See the {{new_technology}} integration in action",
      suggestedMjmlTemplateId: "coldOutreach-demoOffer",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Offer integration-specific demo",
      variables: [
        {
          key: "demo_focus",
          defaultValue: "live integration with your current setup",
          description: "Demo focus",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 35,
    dailyLimit: 105,
    requiresWarming: true,
    minDomainAge: 30,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "new_technology",
      defaultValue: "your new platform",
      description: "Recently adopted technology",
      source: "enrichment",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting link",
      source: "crm",
    },
  ],

  tags: ["prospecting", "trigger", "tech-stack", "integration", "timing"],
  bestFor: ["Tech Ecosystem Sales", "Integration Partners", "Platform Companies"],
  difficulty: "advanced",
  setupTime: "20 minutes",
  inspiredBy: ["BuiltWith", "Siftery", "HG Insights"],
};

export const COLD_VALUE_FIRST: CampaignTemplate = {
  id: "prospect-cold-value-first",
  name: "Value-First Cold Outreach",
  category: "prospecting",
  description:
    "Lead with pure value - share something useful before asking for anything.",
  strategy:
    "Give before you ask. This approach builds reciprocity by leading with valuable content, insights, or tools. Lavender.ai data shows value-first emails get 40% more responses than ask-first emails. The key is genuinely useful content, not gated assets.",
  icon: "GiftOutlined",
  color: "#ec4899",
  targetAudience: "generic",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "20-30% open rate",
    "5-10% reply rate",
    "Builds trust before selling",
    "Higher quality relationships",
  ],

  bestPractices: [
    "Share genuinely useful content without asking for anything in email 1",
    "The value should be consumable in 2-3 minutes",
    "Personalize the value to their role/industry",
    "Don't gate the content behind forms",
    "Only ask for a meeting after providing value",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "referral", "event"],
    companySize: ["11-50", "51-200", "201-500", "501-1000"],
    industries: ["SaaS", "Technology", "E-commerce", "Professional Services"],
    jobTitles: ["VP", "Director", "Head of", "Manager"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Pure Value",
      subject: "{{first_name}}, thought you'd find this useful",
      suggestedMjmlTemplateId: "coldOutreach-valueFirst",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Share valuable insight or resource with no ask",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "value_item",
          defaultValue: "quick analysis of your website's engagement opportunities",
          description: "The valuable thing you're sharing",
        },
        {
          key: "value_description",
          defaultValue: "I noticed 3 quick wins that could improve your conversion rate",
          description: "Brief description of the value",
        },
      ],
    },
    {
      name: "Day 4 - Additional Value",
      subject: "One more thing for {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-moreValue",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Provide additional value, still no hard ask",
      variables: [
        {
          key: "additional_value",
          defaultValue: "benchmark data for companies in your space",
          description: "Additional valuable content",
        },
      ],
    },
    {
      name: "Day 8 - Soft Connection",
      subject: "{{first_name}}, curious if this resonated",
      suggestedMjmlTemplateId: "coldOutreach-softAsk",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "First soft ask for conversation after providing value",
      variables: [
        {
          key: "conversation_topic",
          defaultValue: "how you're thinking about customer engagement",
          description: "Topic for conversation",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 50,
    dailyLimit: 150,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "Account Executive",
      description: "Sales rep's title",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting link",
      source: "crm",
    },
  ],

  tags: ["prospecting", "value-first", "relationship", "trust-building"],
  bestFor: ["Relationship Sales", "Long Sales Cycles", "High-Value Deals"],
  difficulty: "beginner",
  setupTime: "15 minutes",
  inspiredBy: ["Lavender.ai", "Jill Konrath", "Challenger Sale"],
};

export const PERSONALIZED_VIDEO: CampaignTemplate = {
  id: "prospect-personalized-video",
  name: "Personalized Video Outreach",
  category: "prospecting",
  description:
    "Stand out with personalized video messages in your cold outreach.",
  strategy:
    "Vidyard data shows personalized video emails get 3x higher click-through rates. The key is genuine personalization - mention something specific about their company on camera. Keep videos under 90 seconds. Best for high-value prospects worth the extra effort.",
  icon: "VideoCameraOutlined",
  color: "#ef4444",
  targetAudience: "enterprise",
  personalizationLevel: "hyper",

  expectedOutcomes: [
    "30-45% open rate",
    "8-15% reply rate",
    "Memorable first impression",
    "Faster relationship building",
  ],

  bestPractices: [
    "Keep videos under 90 seconds",
    "Mention their company/name in first 5 seconds",
    "Show their website or LinkedIn on screen",
    "Use an engaging thumbnail with their name",
    "Reserve for high-value prospects (worth the effort)",
  ],

  segmentation: {
    leadSource: ["linkedin", "referral", "event"],
    companySize: ["201-500", "501-1000", "1000+"],
    industries: ["Enterprise Software", "Financial Services", "Healthcare"],
    jobTitles: ["C-Level", "VP", "Director"],
    requiredFields: ["first_name", "company_name", "email", "linkedin_url"],
  },

  emails: [
    {
      name: "Day 0 - Video Introduction",
      subject: "{{first_name}}, made this for you [VIDEO]",
      suggestedMjmlTemplateId: "coldOutreach-videoIntro",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Personalized video introducing yourself and value prop",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "video_url",
          defaultValue: "{{video_link}}",
          description: "Personalized video URL",
          required: true,
        },
        {
          key: "video_thumbnail_url",
          defaultValue: "{{thumbnail_link}}",
          description: "Video thumbnail with their name",
        },
      ],
    },
    {
      name: "Day 4 - Video Follow-up",
      subject: "Did you catch my video, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-videoFollowup",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Text follow-up referencing the video",
      variables: [
        {
          key: "video_summary",
          defaultValue: "how AI chatbots could help {{company_name}} scale customer engagement",
          description: "Summary of video content",
        },
      ],
    },
    {
      name: "Day 8 - Alternative Format",
      subject: "{{first_name}}, prefer a quick call instead?",
      suggestedMjmlTemplateId: "coldOutreach-alternativeOffer",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Offer alternative engagement method",
      variables: [
        {
          key: "call_duration",
          defaultValue: "15 minutes",
          description: "Suggested call length",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 20,
    dailyLimit: 60,
    requiresWarming: true,
    minDomainAge: 30,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "video_url",
      defaultValue: "{{video_link}}",
      description: "Personalized video URL",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting link",
      source: "crm",
    },
  ],

  tags: ["prospecting", "video", "personalized", "high-touch", "enterprise"],
  bestFor: ["Enterprise Sales", "Strategic Accounts", "High-Value Prospects"],
  difficulty: "advanced",
  setupTime: "30 minutes",
  inspiredBy: ["Vidyard", "Loom", "BombBomb"],
};

export const PROSPECTING_TEMPLATES = [
  PROBLEM_AWARE_OPENER,
  SOLUTION_AWARE_INTRO,
  COMPETITOR_DISPLACEMENT,
  TRIGGER_FUNDING,
  TRIGGER_HIRING,
  TRIGGER_TECH_CHANGE,
  COLD_VALUE_FIRST,
  PERSONALIZED_VIDEO,
];
